import { useCallback, useState, useRef, useEffect } from 'react';
import { State, useScript } from '@/hooks/useScript';

const GOOGLE_CLIENT_ID =
  '1092568288730-ec49d3un0ocr8kvld567ls2kea92f50t.apps.googleusercontent.com';

export function useGoogleOneTap({
  enabled,
  onResolve,
}: {
  enabled: boolean;
  onResolve: (token: string) => void;
}) {
  const hasInitialized = useRef(false);

  const [error, setError] =
    useState<google.accounts.oauth2.ClientConfigError>();

  const handleCallback = useCallback(
    (response: google.accounts.id.CredentialResponse) => {
      onResolve(response.credential);
    },
    [onResolve]
  );

  const onReady = useCallback(() => {
    try {
      if (!hasInitialized.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCallback,
          auto_select: true,
          cancel_on_tap_outside: false,
          use_fedcm_for_prompt: false,
        });

        window.google.accounts.id.prompt();
        hasInitialized.current = true;
      }
    } catch (error) {
      setError({
        name: 'error',
        type: 'unknown',
        message: 'An unknown error has occurred',
      });
    }
  }, [handleCallback]);

  const onError = useCallback(() => {
    setError({
      name: 'error',
      type: 'unknown',
      message: 'An unknown error has occurred',
    });
  }, []);

  const [status, setStatus] = useState<'loading' | 'ready'>('loading');

  const scriptState = useScript('https://accounts.google.com/gsi/client', {
    crossOrigin: undefined,
    enabled,
  });

  useEffect(() => {
    switch (scriptState) {
      case State.ERROR:
        onError();
        break;
      case State.READY:
        onReady();
        setStatus('ready');
        break;
    }
  }, [scriptState, onReady, onError]);

  if (typeof error !== 'undefined') {
    return { status: 'error', error } as const;
  }

  return { status };
}
